import { MERCH_DOWNLOAD_WORDS, minorToMajor, vendorDeliveryFeeMinor } from "@libs/platform-wire";
import type { Purchase } from "@libs/platform-wire/purchase";

/**
 * What an access page says a purchase costs, from the purchase itself
 * (LIS-1173): its cheapest item on sale with its booking fee and the
 * order's fees ("From" where items cost differently), and the postage of
 * that item where the listing's own ladder prices it. A store's quoted
 * delivery, postage not measured yet, a download its store emails, and
 * tickets carry none. Null with nothing on sale.
 */
export function purchaseHeadline(purchase: Pick<Purchase, "picker" | "fees" | "delivery" | "currency">): {
  price: { amount: number; currency: string; from: boolean };
  deliveryMinor: number | null;
} | null {
  const items = purchase.picker.items
    .filter((item) => item.available)
    .map((item) => {
      const subtotal = item.priceMinor + item.feeMinor;
      const fees = subtotal <= 0 ? 0 : purchase.fees.orderFeeMinor + vendorDeliveryFeeMinor(purchase.fees.ladder, subtotal);
      return { priceMinor: subtotal + fees, subtotal };
    });
  if (!items.length) return null;
  const cheapest = items.reduce((low, item) => (item.priceMinor < low.priceMinor ? item : low));
  const delivery = purchase.delivery;
  let deliveryMinor: number | null;
  switch (delivery.kind) {
    case "ladder":
      deliveryMinor = vendorDeliveryFeeMinor(delivery.tiers, cheapest.subtotal);
      break;
    case "none":
    case "quoted":
    case "unmeasured":
    case "emailed":
      deliveryMinor = null;
      break;
  }
  return {
    price: {
      amount: minorToMajor(cheapest.priceMinor, purchase.currency),
      currency: purchase.currency,
      from: new Set(items.map((item) => item.priceMinor)).size > 1,
    },
    deliveryMinor,
  };
}

/** What an order's line says it holds ("2 × M, 1 × L"), on Link in Bio and
 *  in the app alike; a download its store emails says it is one ("1 × My
 *  Fault · Digital download"), where a posted order says its delivery. */
export function orderLinesWords(
  lines: readonly { quantity: number; name: string }[],
  purchase: Pick<Purchase, "delivery">,
): string {
  const held = lines.map((line) => `${line.quantity} × ${line.name}`).join(", ");
  return purchase.delivery.kind === "emailed" ? `${held} · ${MERCH_DOWNLOAD_WORDS.label}` : held;
}
